// /**
//  * Orders details - list
//  */

// React
import React, { useEffect, useState } from 'react';

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Grid,
  TextField,
  Divider,
  Button,
  ButtonGroup,
} from '@material-ui/core';
// import {ToggleButton, ToggleButtonGroup} from '@mui/material';
// Icon
import Icon from '@mdi/react';
import {
  mdiBarcodeScan,
  mdiChevronDown,
  mdiChevronUp,
  mdiPackageVariant,
  mdiDeleteOutline,
  mdiClose,
} from '@mdi/js';

// Helper
import { checkMatchMedia } from '../../../utils/helpers';

// Type
import { Item } from '../interfaces';
import theme from '../../../theme';
import { Product } from '../../parcelInventoryItems/interfaces';
import OrderItemDetail from '../orderItemDetail';
import { useForm } from 'react-hook-form';
import { mdiNewspaper } from '@mdi/js/commonjs/mdi';

type Data = Item;
type Props = {
  setOpenUnits: () => void;
  print: () => void;
  confirm: () => void;
  packItem: (serial: string) => void;
  serialNumber: boolean;
  data: any;
};
type Ord = 'asc' | 'desc';
interface HeadCell {
  id: keyof Data;
  label: string;
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  print: () => void;
  confirm: () => void;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  setOpenUnits: () => void;
  packItem: (serial: string) => void;
  order: Ord;
  orderBy: string;
  serialNumber: boolean;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      whiteSpace: 'nowrap',
      color: theme.palette.common.white,
      maxWidth: '200px',
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      whiteSpace: 'nowrap',
      fontSize: 14,
    },
  })
)(TableCell);
const ToggleButton = (props: any) => {
  const btnValues = [0.5, 1, 1.5, 2];
  return (
    <ButtonGroup>
      {btnValues.map((btnValue, index) => {
        return (
          <Button
            variant="contained"
            color={props.value == btnValue ? 'primary' : 'secondary'}
            value={props.value}
            key={index}
            onClick={() => props.clickHandler(btnValue)}
          >
            {btnValue}
          </Button>
        );
      })}
    </ButtonGroup>
  );
};

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  })
)(TableRow);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: '40px',
      '& td.MuiTableCell-root': {
        padding: '5px',
      },
      '& .MuiOutlinedInput-input': {
        padding: '10px 0px',
        textAlign: 'center',
      },
    },
    parentRow: {
      '& > td': {
        fontWeight: 500,
        fontSize: '15px',
      },
    },
    paper: {
      position: "relative",
      width: '100%',
    },
    table: {
      // minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    textArea: {
      width: '100%',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    container: {
      maxHeight: 540,
      borderRadius: '4px',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    detail: {
      backgroundColor: theme.palette.gray.main,
      padding: '5px',
      width: '100%',
    },
    secondDetail: {
      backgroundColor: '#F2F2F2',
      padding: '5px',
      borderRadius: '5px',
      // width: '100%',
    },
    direction: {
      direction: 'ltr',
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const classes = useStyles();
  const { order, orderBy, onRequestSort, print, confirm, consumableHandler, addPackage, rowValue } = props;

  const { register, handleSubmit, setValue, getValues, watch } = useForm({
    defaultValues: {
      inventoryItemSerial: '',
    },
  });
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const headCells: HeadCell[] = [
    { id: 'productName', label: 'آیتم' },
    { id: 'count', label: 'گرم' },
  ];

  useEffect(() => {
    setValue('serial', '');
  }, [props.serialNumber]);

  const inventorySubmitHanler = () => {
    const inventoryItemSerial = getValues('inventoryItemSerial');
    if(inventoryItemSerial === "print") {
      print()
      setValue("inventoryItemSerial", "")
    }else if(inventoryItemSerial === "confirm") {
      confirm()
      setValue("inventoryItemSerial", "")
    }else if(inventoryItemSerial.startsWith("P-")) {
      addPackage(inventoryItemSerial)
    }else if(inventoryItemSerial.startsWith("M-")) {
      const splitedSerial = inventoryItemSerial.split("-")
      consumableHandler(splitedSerial[2], splitedSerial[1], rowValue.id)
    }else {
      props.addInventoryItemHandler(props.rowValue, inventoryItemSerial);
    }
  };
  return (
    <TableHead>
      <StyledTableRow>
        <StyledTableCell align="left">
          <form
            style={{ width: '320px', marginLeft: '0', textAlign: 'center' }}
            onSubmit={handleSubmit(inventorySubmitHanler)}
          >
            <Grid
              className={classes.secondDetail}
              container
              spacing={2}
              alignItems="center"
            >
              <Grid item xs={9} alignItems="center">
                <span style={{ margin: '0 10px' }}>
                  <Icon path={mdiBarcodeScan} size={1} color="black" />
                </span>
                <span>
                  <TextField
                    fullWidth
                    id="serial"
                    name="inventoryItemSerial"
                    size="small"
                    inputRef={register({
                      required: true,
                    })}
                    autoFocus
                    label="سریال آیتم"
                  />
                </span>
              </Grid>
              <Grid item xs={3}>
                <IconButton
                  aria-label="details"
                  type="submit"
                  size="small"
                  title="بسته بندی آیتم"
                  data-cy="details"
                >
                  <Icon
                    path={mdiPackageVariant}
                    size={1}
                    color={theme.palette.info.main}
                  />
                </IconButton>
              </Grid>
            </Grid>
          </form>
        </StyledTableCell>

        <StyledTableCell
          key={headCells[0].id}
          align="center"
          sortDirection={orderBy === headCells[0].id ? order : false}
          data-cy={headCells[0].id}
        >
          <span
            style={{
              backgroundColor: '#E4E4E4',
              padding: '10px',
              color: 'black',
              borderRadius: '5px',
            }}
          >
            {props.rowValue.inventoryItems
              ? props.rowValue.inventoryItems.length
              : 0}
          </span>
          &nbsp;
          <TableSortLabel
            active={orderBy === headCells[0].id}
            direction={orderBy === headCells[0].id ? order : 'asc'}
            onClick={createSortHandler(headCells[0].id)}
            classes={{
              // Override with the active class if this is the selected column or inactive otherwise
              icon:
                orderBy === headCells[0].id
                  ? classes.activeSortIcon
                  : classes.inactiveSortIcon,
            }}
            IconComponent={() => (
              <Icon
                path={
                  orderBy === headCells[0].id && order === 'desc'
                    ? mdiChevronDown
                    : mdiChevronUp
                }
                size={0.7}
                className={
                  orderBy === headCells[0].id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon
                }
              />
            )}
          >
            {headCells[0].label}
          </TableSortLabel>
        </StyledTableCell>
        <StyledTableCell
          key={headCells[1].id}
          align="center"
          sortDirection={orderBy === headCells[1].id ? order : false}
          data-cy={headCells[1].id}
        >
          <span
            style={{
              backgroundColor: '#E4E4E4',
              padding: '10px',
              color: 'black',
              borderRadius: '5px',
            }}
          >
            {props.rowValue.totalWeight}
          </span>
          &nbsp;
          <TableSortLabel
            active={orderBy === headCells[1].id}
            direction={orderBy === headCells[1].id ? order : 'asc'}
            onClick={createSortHandler(headCells[1].id)}
            classes={{
              // Override with the active class if this is the selected column or inactive otherwise
              icon:
                orderBy === headCells[1].id
                  ? classes.activeSortIcon
                  : classes.inactiveSortIcon,
            }}
            IconComponent={() => (
              <Icon
                path={
                  orderBy === headCells[1].id && order === 'desc'
                    ? mdiChevronDown
                    : mdiChevronUp
                }
                size={0.7}
                className={
                  orderBy === headCells[1].id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon
                }
              />
            )}
          >
            {headCells[1].label}
          </TableSortLabel>
        </StyledTableCell>

        <StyledTableCell />
        <StyledTableCell />
        <StyledTableCell />
        <StyledTableCell />
        <StyledTableCell />
        <StyledTableCell />
        <StyledTableCell />
        <StyledTableCell align="center">
          <IconButton
            aria-label="expand row"
            size="medium"
            onClick={props.setBodyOpen}
          >
            {props.open ? (
              <Icon path={mdiChevronUp} size={1} color="white" />
            ) : (
              <Icon path={mdiChevronDown} size={1} color="white" />
            )}
          </IconButton>
        </StyledTableCell>
      </StyledTableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('productId');
  const [open, setOpen] = useState(true);
  const [bubbleValue, setBubbleValue] = useState('');
  const [cellophaneValue, setCellophaneValue] = useState('');
  const [fomValue, setFomValue] = useState('');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <h2>{`${props.data.packageName} - (${props.data.width}*${props.data.height}*${props.data.length})`}</h2>
      <Paper className={classes.paper} elevation={3}>
        <Grid container xs={12} alignItems="center" style={{ padding: "10px" }}>
          <Grid item xs={4} alignItems="center">
            <Grid container alignItems="center" style={{ marginRight: "10px" }}>
            {props.data.consumables && <Grid item>({props.data.consumables.filter(el => el.consumableId === 1)[0]?.amount}) </Grid>}
              <Grid item>{'فوم ضربه گیر:'}</Grid>
              <Grid item>
                <ToggleButton
                  clickHandler={(btnValue: any) => {
                    setFomValue(btnValue);
                  }}
                  value={fomValue}
                />
              </Grid>
              <Grid item>
                <TextField
                  variant="outlined"
                  margin="normal"
                  id="email"
                  dir="rtl"
                  type="number"
                  placeholder="عدد دلخواه"
                  name="cellophaneInput"
                  autoFocus
                  value={fomValue}
                  style={{ width: '100px', margin: 'auto 7px' }}
                  className={classes.direction}
                  onChange={event => {
                    setFomValue(event.target.value);
                  }}
                />
              </Grid>
              <Grid item>
                <Button
                  aria-label="details"
                  size="medium"
                  variant="contained"
                  onClick={() =>
                    props.setConsumableHandler(
                      fomValue,
                      3,
                      props.data.id
                    )
                  }
                >
                  {'ثبت'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4} alignItems="center">
            <Grid container alignItems="center">
              <Grid item>
                <Divider
                  style={{
                    color: 'black',
                    height: '50px',
                    border: '1px #E8E8E8 solid',
                  }}
                  orientation="vertical"
                  variant="middle"
                  flexItem
                />
              </Grid>
              {props.data.consumables && <Grid item>({props.data.consumables.filter(el => el.consumableId === 1)[0]?.amount}) </Grid>}
              <Grid item>{'سلفون:'}</Grid>
              <Grid item>
                <ToggleButton
                  clickHandler={(btnValue: any) => {
                    setCellophaneValue(btnValue);
                  }}
                  value={cellophaneValue}
                />
              </Grid>
              <Grid item>
                <TextField
                  variant="outlined"
                  margin="normal"
                  id="email"
                  dir="rtl"
                  type="number"
                  placeholder="عدد دلخواه"
                  name="cellophaneInput"
                  autoFocus
                  value={cellophaneValue}
                  style={{ width: '100px', margin: 'auto 7px' }}
                  className={classes.direction}
                  onChange={event => {
                    setCellophaneValue(event.target.value);
                  }}
                />
              </Grid>
              <Grid item>
                <Button
                  aria-label="details"
                  size="medium"
                  variant="contained"
                  onClick={() =>
                    props.setConsumableHandler(
                      cellophaneValue,
                      2,
                      props.data.id
                    )
                  }
                >
                  {'ثبت'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4} alignItems="center">
            <Grid container alignItems="center">
              <Grid item>
                <Divider
                  style={{
                    color: 'black',
                    height: '50px',
                    border: '1px #E8E8E8 solid',
                  }}
                  orientation="vertical"
                  variant="middle"
                  flexItem
                />
              </Grid>
              {props.data.consumables && <Grid item>({props.data.consumables.filter(el => el.consumableId === 1)[0]?.amount}) </Grid>}
              <Grid item>{'حبابی:'}</Grid>
              <Grid item>
                <ToggleButton
                  clickHandler={(btnValue: any) => {
                    setBubbleValue(btnValue);
                  }}
                  value={bubbleValue}
                />
              </Grid>
              <Grid item>
                <TextField
                  variant="outlined"
                  margin="normal"
                  id="email"
                  dir="rtl"
                  type="number"
                  placeholder="عدد دلخواه"
                  name="bubbleInput"
                  autoFocus
                  value={bubbleValue}
                  style={{ width: '100px', margin: 'auto 7px' }}
                  className={classes.direction}
                  onChange={event => {
                    setBubbleValue(event.target.value);
                  }}
                />
              </Grid>
              <Grid item>
                <Button
                  aria-label="details"
                  size="medium"
                  variant="contained"
                  onClick={() =>
                    props.setConsumableHandler(bubbleValue, 1, props.data.id)
                  }
                >
                  {'ثبت'}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <IconButton
            onClick={() => {
              props.deleteOrderPackageHandler(props.data.id);
            }}
            style={{ position: "absolute", top: 0, left: 0 }}
            aria-label="expand row"
            size="medium"
          >
            <Icon path={mdiDeleteOutline} size={1.5} color="red" />
          </IconButton>
        </Grid>
        <TableContainer className={classes.container}>
          <Table
            stickyHeader
            className={classes.table}
            aria-labelledby="tableTitle"
            size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              print={props.print}
              confirm={props.confirm}
              addPackage={props.addPackage}
              onRequestSort={handleRequestSort}
              consumableHandler={props.setConsumableHandler}
              addInventoryItemHandler={props.addInventoryItemHandler}
              rowCount={Object.keys(props.data).length}
              rowValue={props.data}
              setBodyOpen={() => {
                setOpen(!open);
              }}
              open={open}
            />
            {open && (
              <TableBody>
                {stableSort(
                  props.data.inventoryItems,
                  getComparator(order, orderBy)
                )
                  .slice()
                  .map((row: any, index: number) => {
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (
                      <Row
                        key={index}
                        row={row}
                        index={index}
                        labelId={labelId}
                        removeInventoryHandler={props.removeInventoryHandler}
                        data={props.data}
                      />
                    );
                  })}
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}

function Row(props: { row: any; labelId: string; index: number }) {
  const { row, labelId, index } = props;
  const classes = useStyles();
  return (
    <>
      <StyledTableRow className={classes.parentRow} key={index}>
        <TableCell id={labelId} scope="row" align="center">
          {'سریال: '}
          {row.serialNumber}
        </TableCell>
        <TableCell align="center" scope="row" data-cy="row-id">
          {row.productName}
        </TableCell>
        <TableCell align="center" scope="row" data-cy="row-id">
          {row.unitCost}
        </TableCell>
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell align="center">
          <>
            <IconButton
              aria-label="cancele"
              title="حذف آیتم از جعبه "
              size="small"
              onClick={() =>
                props.removeInventoryHandler(props.data.id, row.serialNumber)
              }
            >
              <Icon path={mdiClose} size={1} color={theme.palette.error.main} />
            </IconButton>
          </>
        </TableCell>
      </StyledTableRow>
    </>
  );
}
